import { useMutation } from '@apollo/client';
import {
  ICreateScratchpadImageEmbedUploadConfigMutation,
  ICreateScratchpadImageEmbedUploadConfigMutationVariables,
} from '../../../utils/apollo/graphqlSchema';
import { CREATE_SCRATCHPAD_IMAGE_EMBED_UPLOAD_CONFIG } from '../../../utils/apollo/scratchpad/mutations';
import { useCallback, useRef } from 'react';
import { IImageEmbedUploadHandle } from '../../ImageUploady/ImageEmbedUpload';
import { assertNotNil } from '../../../utils/types';
import { ContentSplitResult } from '../ScratchpadContent/useContentSplit';
import { MaybeNil } from '../../../utils/interfaces';

const useScratchpadImageEmbed = (scratchpadId: string) => {
  const [addImageEmbedMutation] = useMutation<
    ICreateScratchpadImageEmbedUploadConfigMutation,
    ICreateScratchpadImageEmbedUploadConfigMutationVariables
  >(CREATE_SCRATCHPAD_IMAGE_EMBED_UPLOAD_CONFIG);

  const imageEmbedAddUploadHandle =
    useRef<IImageEmbedUploadHandle<MaybeNil<ContentSplitResult>>>(null);

  const startImageEmbedEditUpload = useCallback(
    (split: MaybeNil<ContentSplitResult>) => {
      assertNotNil(imageEmbedAddUploadHandle.current);
      imageEmbedAddUploadHandle.current.startUpload(split);
    },
    [],
  );

  const createImageEmbedUploadConfig = useCallback(async () => {
    const resp = await addImageEmbedMutation({
      variables: { input: { scratchpadId } },
    });
    const result = resp.data?.createScratchpadImageEmbedUploadConfig;
    assertNotNil(result?.postUploadConfig);
    assertNotNil(result?.imageEmbed);
    return {
      imageEmbed: result.imageEmbed,
      postUploadConfig: result.postUploadConfig,
    };
  }, [addImageEmbedMutation, scratchpadId]);

  return {
    imageEmbedAddUploadHandle,
    startImageEmbedEditUpload,
    createImageEmbedUploadConfig,
  };
};

export default useScratchpadImageEmbed;
